/**
 * "Look this up properly" — a link out to a web search.
 *
 * Deliberately a plain link and not a feature. Slate's own copy is limited to
 * what two catalogues will state as fact, which is the right constraint for
 * anything that can produce a notification — but it leaves nowhere to go when
 * you simply want to *read* about a thing. A link out costs nothing, can't be
 * wrong, and can't be mistaken for the app making a claim.
 *
 * This is not the designed "check for news" pass, which reads interviews and
 * forum posts and summarises them under a Keep button. That one has a trust
 * boundary to respect. This one just opens a search.
 */

import type { WatchItem } from './model.js';

/** The noun a person would actually type alongside the title. */
function noun(item: WatchItem): string {
  if (item.kind === 'creator') return item.medium === 'book' ? 'author' : 'director';
  if (item.kind === 'series') return item.medium === 'book' ? 'book series' : 'series';
  if (item.medium === 'film') return 'film';
  if (item.medium === 'tv') return 'tv series';
  return 'book';
}

/**
 * Quoting the title keeps "Alecto the Ninth" from being read as three words,
 * which matters most for the titles nobody has heard of — exactly the ones
 * you'd be looking up.
 */
export function webSearchQuery(item: WatchItem): string {
  const parts = [`"${item.title}"`];

  // An author's own name as the byline would just repeat the title.
  if (item.byline && item.kind !== 'creator') parts.push(item.byline);
  parts.push(noun(item));

  // For something not yet dated, the question is nearly always "when", so ask
  // it. For something already out it would only narrow the results wrongly.
  if (!hasDate(item)) parts.push('release date');

  return parts.join(' ');
}

function hasDate(item: WatchItem): boolean {
  const snapshot = item.snapshot;
  if (!snapshot || snapshot.kind !== 'title') return false;
  return Object.values(snapshot.releases).some((d) => d != null);
}

export function webSearchUrl(item: WatchItem): string {
  return `https://www.google.com/search?q=${encodeURIComponent(webSearchQuery(item))}`;
}
